/**
 * Widget State Store
 *
 * Persists widget state (session, messages, open/closed) in the host page's
 * sessionStorage so that state survives same-tab page navigations.
 *
 * All operations are wrapped in try/catch — graceful degradation if storage
 * is unavailable (private browsing edge cases, quota exceeded, etc.).
 */

import type { WidgetPersistedState } from "../types/widget.types.js";

const KEY_PREFIX = "__ethos_widget_state__";
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes staleness guard
const MAX_MESSAGES = 100;
const TRUNCATE_TO = 50;

function storageKey(orgId: string, widgetId: string): string {
  return `${KEY_PREFIX}${orgId}:${widgetId}`;
}

export function loadState(orgId: string, widgetId: string): WidgetPersistedState | null {
  try {
    const raw = sessionStorage.getItem(storageKey(orgId, widgetId));
    if (!raw) return null;
    const state = JSON.parse(raw) as WidgetPersistedState;
    if (Date.now() - state.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(storageKey(orgId, widgetId));
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

export function saveState(orgId: string, widgetId: string, state: WidgetPersistedState): void {
  try {
    const toSave = { ...state };
    if (toSave.messages.length > MAX_MESSAGES) {
      toSave.messages = toSave.messages.slice(-TRUNCATE_TO);
    }
    sessionStorage.setItem(storageKey(orgId, widgetId), JSON.stringify(toSave));
  } catch {
    // Storage full or unavailable — degrade silently.
  }
}

export function clearState(orgId: string, widgetId: string): void {
  try {
    sessionStorage.removeItem(storageKey(orgId, widgetId));
  } catch {
    // Ignore.
  }
}
